/**
 * Empty State Component
 * Display when there's no content to show (empty playlists, no search results, etc.)
 */

import type { LucideIcon } from 'lucide-react';

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  message,
  action,
  className = '',
}: EmptyStateProps) {
  return (
    <div
      className={`
        flex flex-col items-center justify-center
        py-16 px-6 text-center
        ${className}
      `}
    >
      {/* Icon */}
      <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-zinc-500" />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-zinc-300 mb-2">
        {title}
      </h3>

      {/* Message */}
      {message && (
        <p className="text-sm text-zinc-500 max-w-md mb-6">
          {message}
        </p>
      )}

      {/* Action Button */}
      {action && (
        <button
          onClick={action.onClick}
          className="
            px-4 py-2
            bg-emerald-500 hover:bg-emerald-600
            text-white text-sm font-medium
            rounded-lg
            transition-colors
          "
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/**
 * Compact variant for inline use
 */
export interface EmptyStateCompactProps {
  icon: LucideIcon;
  message: string;
  className?: string;
}

export function EmptyStateCompact({
  icon: Icon,
  message,
  className = '',
}: EmptyStateCompactProps) {
  return (
    <div
      className={`
        flex items-center justify-center gap-3
        py-8 px-4 text-center
        ${className}
      `}
    >
      <Icon className="w-5 h-5 text-zinc-500" />
      <p className="text-sm text-zinc-500">{message}</p>
    </div>
  );
}
